// Pure helpers for @mentions in task comments.
//
// Only people involved in the task can be mentioned: the creator, the primary
// assignee and every active assignee row (any role). The Teamlio backend keeps
// the same rule (utils/task_notifier.resolve_comment_mentions), so a mention can
// never notify someone outside the task.
import type { Task, TaskAssignee } from '../api/tasks';

export interface MentionCandidate {
  id: string;
  name: string;
  email?: string;
  /** Readable labels for this person's part in the task, e.g. ["Creator", "Reviewer"]. */
  roles: string[];
}

type MentionTask = Pick<Task, 'created_by'> &
  Partial<Pick<Task, 'created_by_name' | 'primary_assignee_id' | 'primary_assignee_name'>>;

type MentionAssignee = Pick<TaskAssignee, 'user_id' | 'role_in_task' | 'is_active'> &
  Partial<Pick<TaskAssignee, 'employee_name'>>;

type LookupUser = (id: string) => { name?: string | null; email?: string | null } | null | undefined;

// PREPAROR is the stored enum; the UI says "Preparer".
const ROLE_LABELS: Record<string, string> = {
  OWNER: 'Owner',
  COLLABORATOR: 'Collaborator',
  REVIEWER: 'Reviewer',
  PREPAROR: 'Preparer',
  WATCHER: 'Watcher',
};

export const taskRoleLabel = (role: string): string =>
  ROLE_LABELS[role] ?? role.charAt(0) + role.slice(1).toLowerCase();

export function buildMentionCandidates({
  task,
  assignees,
  currentUserId,
  lookupUser,
}: {
  task: MentionTask;
  assignees: MentionAssignee[];
  currentUserId?: string | null;
  lookupUser?: LookupUser;
}): MentionCandidate[] {
  const people = new Map<string, { names: (string | null | undefined)[]; roles: string[] }>();
  const note = (id: string | null | undefined, role: string, name?: string | null) => {
    if (!id || id === currentUserId) return;
    const entry = people.get(id) ?? { names: [], roles: [] };
    if (!entry.roles.includes(role)) entry.roles.push(role);
    entry.names.push(name);
    people.set(id, entry);
  };

  note(task.created_by, 'Creator', task.created_by_name);
  const active = assignees.filter((a) => a.is_active);
  active.forEach((a) => note(a.user_id, taskRoleLabel(a.role_in_task), a.employee_name));
  const primary = task.primary_assignee_id;
  if (primary && !active.some((a) => a.user_id === primary)) {
    note(primary, 'Assignee', task.primary_assignee_name);
  }

  const out: MentionCandidate[] = [];
  people.forEach((entry, id) => {
    const found = lookupUser?.(id) ?? null;
    const name = [...entry.names, found?.name].map((n) => (n ?? '').trim()).find(Boolean);
    if (!name) return; // can't write "@name" for someone we can't name
    out.push({ id, name, email: found?.email || undefined, roles: entry.roles });
  });
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

const MAX_QUERY_LENGTH = 40;

/** The "@query" being typed at the caret, or null when the caret isn't in one. */
export function findMentionQuery(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const start = before.lastIndexOf('@');
  if (start === -1) return null;
  // "@" must start a word — "yash@acme" is an email, not a mention.
  if (start > 0 && !/[\s([{"']/.test(before[start - 1])) return null;
  const query = before.slice(start + 1);
  if (query.length > MAX_QUERY_LENGTH || /^\s/.test(query) || /[\r\n]/.test(query)) return null;
  return { start, query };
}

export function filterMentionCandidates(
  people: MentionCandidate[],
  query: string,
  limit = 8,
): MentionCandidate[] {
  const q = query.toLowerCase();
  if (!q) return people.slice(0, limit);
  const leading: MentionCandidate[] = [];
  const rest: MentionCandidate[] = [];
  people.forEach((p) => {
    const name = p.name.toLowerCase();
    if (name.startsWith(q)) leading.push(p);
    else if (name.includes(q) || (p.email ?? '').toLowerCase().startsWith(q)) rest.push(p);
  });
  return [...leading, ...rest].slice(0, limit);
}

/** Replace the "@query" in text[start, caret) with "@Full Name ". */
export function insertMention(
  text: string,
  start: number,
  caret: number,
  name: string,
): { text: string; caret: number } {
  const after = text.slice(caret);
  const spaced = /^\s/.test(after);
  const mention = `@${name}${spaced ? '' : ' '}`;
  return {
    text: text.slice(0, start) + mention + after,
    caret: start + mention.length + (spaced ? 1 : 0),
  };
}

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&');
// A name only counts when it isn't the start of a longer word ("@Yash" ≠ "@Yashika").
const NAME_END = '(?![\\p{L}\\p{N}_])';
// Fallback for handles in older comments ("@rahul"); never ends on "." or "-".
const HANDLE = '[\\p{L}\\p{N}_](?:[\\p{L}\\p{N}._-]*[\\p{L}\\p{N}_])?';

// Longest first, so "@Yash Pandey" wins over "@Yash".
function namesPattern(names: string[]): string {
  return Array.from(new Set(names.map((n) => n.trim()).filter(Boolean)))
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp)
    .join('|');
}

/** Ids of the people whose "@Full Name" appears in the text, in order of appearance. */
export function extractMentionIds(text: string, people: MentionCandidate[]): string[] {
  const names = namesPattern(people.map((p) => p.name));
  if (!names || !text) return [];
  const re = new RegExp(`@(${names})${NAME_END}`, 'giu');
  const ids: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const hit = m[1].toLowerCase();
    people.forEach((p) => {
      if (p.name.trim().toLowerCase() === hit && !ids.includes(p.id)) ids.push(p.id);
    });
  }
  return ids;
}

/** Split comment text into plain and mention segments for rendering. */
export function splitMentions(text: string, names: string[]): Array<{ text: string; mention: boolean }> {
  const known = namesPattern(names);
  const mention = known ? `(?:${known})${NAME_END}|${HANDLE}` : HANDLE;
  const re = new RegExp(`(^|[\\s([{"'])(@(?:${mention}))`, 'giu');
  const parts: Array<{ text: string; mention: boolean }> = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const at = m.index + m[1].length;
    if (at > last) parts.push({ text: text.slice(last, at), mention: false });
    parts.push({ text: m[2], mention: true });
    last = at + m[2].length;
  }
  if (last < text.length) parts.push({ text: text.slice(last), mention: false });
  return parts;
}

export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  const last = words.length > 1 ? words[words.length - 1].charAt(0) : '';
  return (words[0].charAt(0) + last).toUpperCase();
}
